/**
 * This is the stellation data for the rhombic triacontahedron, i.e.
 * facet vertices, facets (with accompanying data), and stellation list.
 * This code is protected under the MIT license (see the LICENSE file).
 * @author Zenzicubic
 */
import { mat3, vec3 } from '../../threedengine/threedmath';

// Constants
const rt5 = Math.sqrt(5);
const phi = 0.5 + 0.5 * rt5, iphi = phi - 1;

const c0 = 0.5 * phi, c1 = c0 - 0.5, c2 = 1 + phi;

const c3 = 1 + rt5, c4 = 2 + rt5, c5 = 3 + rt5, 
      c6 = 4 + rt5, c7 = rt5 - 2;

const c8 = 2 - phi, c9 = 2 * c8, c10 = 1 + c8,
      c11 = c2 * c2 - 1, c12 = c11 - 1, c13 = c11 - 2,
      c14 = c13 - 2, c15 = c14 - 1, c16 = c13 - 1,
      c17 = c9 + 1;

const c18 = 2 * rt5 - 3, c19 = c18 + 6, c20 = 2 + phi,
      c21 = rt5 - 1;

// Vertices of facets
export const facetVerts = [
    vec3(iphi, -c3, -1),
    vec3(c3, -phi, -1),
    vec3(phi, -c2, -1),
    vec3(-1, -c4, -1),
    vec3(-c4, -c6, -1),
    vec3(-c8, -c13, -1),
    vec3(0, -c20, -1),
    vec3(-c2, -c5, -1),
    vec3(c4, -1, -1),
    vec3(c2, -2, -1),
    vec3(1, -3, -1),
    vec3(rt5, -rt5, -1),
    vec3(c11, 0, -1),
    vec3(c19, 1, -1),
    vec3(c20, -c10, -1),
    vec3(phi, -iphi, -1),
    vec3(c21, -phi, -1),
    vec3(0, -c12, -1),
    vec3(c8, -c13, -1),
    vec3(c2, 2, -1),
    vec3(2, c8, -1),
    vec3(c14, 0, -1),
    vec3(1, -rt5, -1),
    vec3(-1, -c19, -1),
    vec3(c3, c20, -1),
    vec3(c4, c6, -1),
    vec3(c16, c2, -1),
    vec3(rt5, 1, -1),
    vec3(c17, -c7, -1),
    vec3(c18, -1, -1),
    vec3(c10, -c21, -1),
    vec3(iphi, 0, -1),
    vec3(-c10, c21, -1),
    vec3(-c2, 2, -1),
    vec3(-c8, iphi, -1),
    vec3(0, c8, -1),
    vec3(rt5, -1, -1),
    vec3(1, -c7, -1),
    vec3(-1, 1, -1),
    vec3(-iphi, c3, -1),
    vec3(c2, -c5, -1),
    vec3(0, phi, -1),
    vec3(c8, iphi, -1),
    vec3(1, -1, -1),
    vec3(-1, c4, -1),
    vec3(c7, 1, -1),
    vec3(c9, -c8, -1),
    vec3(-iphi, -c3, -1),
    vec3(1, 1, -1),
    vec3(c21, phi, -1),
    vec3(0, -phi, -1),
    vec3(c7, -1, -1),
    vec3(phi, c2, -1),
    vec3(c2, c5, -1),
    vec3(c9, c8, -1),
    vec3(c8, -iphi, -1),
    vec3(-c14, 0, -1),
    vec3(-c2, -2, -1),
    vec3(-rt5, -1, -1),
    vec3(-1, rt5, -1),
    vec3(0, c12, -1),
    vec3(1, c19, -1),
    vec3(-c21, phi, -1),
    vec3(-c17, c7, -1),
    vec3(-phi, iphi, -1),
    vec3(-c8, c13, -1),
    vec3(-2, -c8, -1),
    vec3(-c18, 1, -1),
    vec3(-c3, -c20, -1),
    vec3(-c16, -c2, -1),
    vec3(-c21, -phi, -1),
    vec3(-phi, -iphi, -1),
    vec3(-c3, c20, -1),
    vec3(1, -c19, -1),
    vec3(-1, -rt5, -1),
    vec3(-2, c8, -1),
    vec3(-c4, c6, -1),
    vec3(-c17, -c7, -1),
    vec3(-rt5, 1, -1),
    vec3(-c16, c2, -1),
    vec3(-c18, -1, -1),
    vec3(-c10, -c21, -1),
    vec3(-phi, -c2, -1),
    vec3(-c3, -phi, -1),
    vec3(c4, -c6, -1),
    vec3(1, -c4, -1),
    vec3(-1, -3, -1),
    vec3(-c4, -1, -1),
    vec3(-c11, 0, -1),
    vec3(-c20, -c10, -1),
    vec3(-c19, 1, -1),
    vec3(-rt5, -rt5, -1),
    vec3(-iphi, 0, -1),
    vec3(iphi, c3, -1),
    vec3(1, c4, -1),
    vec3(-1, -1, -1),
    vec3(-c9, -c8, -1),
    vec3(-c7, 1, -1),
    vec3(c10, c21, -1),
    vec3(-1, -c7, -1),
    vec3(phi, iphi, -1),
    vec3(-phi, c2, -1),
    vec3(iphi, c21, -1),
    vec3(c2, 0, -1),
    vec3(1, rt5, -1),
    vec3(-1, c19, -1),
    vec3(c16, -c2, -1),
    vec3(c8, c13, -1),
    vec3(c18, 1, -1),
    vec3(2, -c8, -1),
    vec3(c3, -c20, -1),
    vec3(c17, c7, -1),
    vec3(1, -c18, -1),
    vec3(1, 3, -1),
    vec3(1, c18, -1),
    vec3(1, c7, -1),
    vec3(-1, 3, -1),
    vec3(-1, c7, -1),
    vec3(-1, c18, -1),
    vec3(-1, -c18, -1),
    vec3(-c8, -iphi, -1),
    vec3(-c2, c5, -1),
    vec3(-c9, c8, -1),
    vec3(-c7, -1, -1),
    vec3(-c20, c10, -1),
    vec3(-c10, 0, -1),
    vec3(-3, 1, -1),
    vec3(iphi, -c21, -1),
    vec3(0, -c15, -1),
    vec3(-iphi, c21, -1),
    vec3(-c2, 0, -1),
    vec3(0, c20, -1),
    vec3(-c4, 1, -1),
    vec3(-rt5, rt5, -1),
    vec3(-c19, -1, -1),
    vec3(-c3, phi, -1),
    vec3(rt5, rt5, -1),
    vec3(c4, 1, -1),
    vec3(c19, -1, -1),
    vec3(c3, phi, -1),
    vec3(c20, c10, -1),
    vec3(0, c15, -1),
    vec3(-3, -1, -1),
    vec3(3, 1, -1),
    vec3(3, -1, -1),
    vec3(c10, 0, -1),
    vec3(-iphi, -c21, -1),
    vec3(0, -c8, -1),
];

// Matrices taking one face of the triacontahedron to all the others
export const matrices = [
    mat3([1, 0, 0], [0, -1, 0], [0, 0, 1]),
    mat3([0.5, c0, c1], [c0, -c1, -0.5], [c1, -0.5, c0]),
    mat3([-c1, 0.5, c0], [0.5, c0, -c1], [c0, -c1, 0.5]),
    mat3([0.5, c0, c1], [-c0, c1, 0.5], [-c1, 0.5, -c0]),
    mat3([-c1, 0.5, c0], [-0.5, -c0, c1], [-c0, c1, -0.5]),
    mat3([-c1, -0.5, c0], [0.5, -c0, -c1], [-c0, -c1, -0.5]),
    mat3([c1, -0.5, -c0], [-0.5, -c0, c1], [c0, -c1, 0.5]),
    mat3([c1, 0.5, -c0], [0.5, -c0, -c1], [c0, c1, 0.5]),
    mat3([-0.5, c0, -c1], [c0, c1, -0.5], [c1, 0.5, c0]),
    mat3([c1, 0.5, -c0], [-0.5, c0, c1], [-c0, -c1, -0.5]),
    mat3([-0.5, c0, -c1], [-c0, -c1, 0.5], [-c1, -0.5, -c0]),
    mat3([-1, 0, 0], [0, -1, 0], [0, 0, -1]),
    mat3([c0, c1, 0.5], [-c1, -0.5, c0], [-0.5, c0, c1]),
    mat3([c1, 0.5, c0], [0.5, -c0, c1], [-c0, -c1, 0.5]),
    mat3([0, 0, 1], [1, 0, 0], [0, -1, 0]),
    mat3([0, 0, -1], [-1, 0, 0], [0, -1, 0]),
    mat3([-c1, 0.5, -c0], [-0.5, -c0, -c1], [c0, -c1, -0.5]),
    mat3([-c0, c1, -0.5], [c1, -0.5, -c0], [0.5, c0, -c1]),
    mat3([c0, -c1, -0.5], [c1, -0.5, c0], [0.5, c0, c1]),
    mat3([0.5, c0, -c1], [c0, -c1, 0.5], [-c1, 0.5, c0]),
    mat3([-0.5, c0, c1], [c0, c1, 0.5], [-c1, -0.5, c0]),
    mat3([c0, c1, -0.5], [c1, 0.5, c0], [-0.5, c0, -c1]),
    mat3([0, 1, 0], [0, 0, 1], [-1, 0, 0]),
    mat3([-c0, c1, 0.5], [c1, -0.5, c0], [-0.5, -c0, -c1]),
    mat3([c0, c1, -0.5], [-c1, -0.5, -c0], [0.5, -c0, c1]),
    mat3([0, 1, 0], [0, 0, -1], [1, 0, 0]),
    mat3([-c0, c1, 0.5], [-c1, 0.5, -c0], [0.5, c0, c1]),
    mat3([0.5, c0, -c1], [-c0, c1, -0.5], [c1, -0.5, -c0]),
    mat3([-0.5, c0, c1], [-c0, -c1, -0.5], [c1, 0.5, -c0]),
    mat3([-c0, -c1, 0.5], [-c1, -0.5, -c0], [-0.5, c0, -c1]),
];

// The facets, with labels and chiralities
export const facets = [
    {indices: [147, 31, 35, 92], type: "0", chirality: null},
    {indices: [35, 34, 92], type: "1a", chirality: "L"},
    {indices: [35, 31, 42], type: "1a", chirality: "R"},
    {indices: [147, 55, 31], type: "1a", chirality: "L"},
    {indices: [92, 120, 147], type: "1a", chirality: "R"},
    {indices: [147, 120, 128, 55], type: "2a", chirality: null},
    {indices: [42, 141, 34, 35], type: "2a", chirality: null},
    {indices: [92, 96, 120], type: "2b", chirality: "L"},
    {indices: [34, 122, 92], type: "2b", chirality: "R"},
    {indices: [31, 54, 42], type: "2b", chirality: "L"},
    {indices: [31, 55, 46], type: "2b", chirality: "R"},
    {indices: [123, 128, 120], type: "3a", chirality: "L"},
    {indices: [34, 141, 97], type: "3a", chirality: "R"},
    {indices: [42, 45, 141], type: "3a", chirality: "L"},
    {indices: [55, 128, 51], type: "3a", chirality: "R"},
    {indices: [120, 96, 95], type: "3b", chirality: "L"},
    {indices: [38, 122, 34], type: "3b", chirality: "R"},
    {indices: [48, 42, 54], type: "3b", chirality: "L"},
    {indices: [43, 46, 55], type: "3b", chirality: "R"},
    {indices: [92, 122, 117], type: "3c", chirality: "L"},
    {indices: [92, 99, 96], type: "3c", chirality: "R"},
    {indices: [31, 46, 37], type: "3c", chirality: "L"},
    {indices: [115, 54, 31], type: "3c", chirality: "R"},
    {indices: [128, 123, 51], type: "4a", chirality: null},
    {indices: [45, 97, 141], type: "4a", chirality: null},
    {indices: [43, 55, 51], type: "4b", chirality: "L"},
    {indices: [123, 120, 95], type: "4b", chirality: "R"},
    {indices: [38, 34, 97], type: "4b", chirality: "L"},
    {indices: [45, 42, 48], type: "4b", chirality: "R"},
    {indices: [38, 117, 122], type: "4c", chirality: "L"},
    {indices: [96, 99, 95], type: "4c", chirality: "R"},
    {indices: [37, 46, 43], type: "4c", chirality: "L"},
    {indices: [48, 54, 115], type: "4c", chirality: "R"},
    {indices: [115, 31, 37], type: "4d", chirality: null},
    {indices: [99, 92, 117], type: "4d", chirality: null},
    {indices: [50, 51, 123], type: "5a", chirality: null},
    {indices: [97, 45, 41], type: "5a", chirality: null},
    {indices: [51, 127, 43], type: "5b", chirality: "L"},
    {indices: [48, 102, 45], type: "5b", chirality: "R"},
    {indices: [97, 129, 38], type: "5b", chirality: "L"},
    {indices: [95, 146, 123], type: "5b", chirality: "R"},
    {indices: [37, 43, 15], type: "5c", chirality: "L"},
    {indices: [99, 71, 95], type: "5c", chirality: "R"},
    {indices: [117, 38, 64], type: "5c", chirality: "L"},
    {indices: [48, 115, 100], type: "5c", chirality: "R"},
    {indices: [117, 125, 99], type: "5d", chirality: null},
    {indices: [115, 37, 145], type: "5d", chirality: null},
    {indices: [41, 129, 97], type: "6a", chirality: "L"}, 
    {indices: [41, 45, 102], type: "6a", chirality: "R"},
    {indices: [50, 127, 51], type: "6a", chirality: "L"}, 
    {indices: [123, 146, 50], type: "6a", chirality: "R"},
    {indices: [119, 146, 95], type: "6b", chirality: "L"}, 
    {indices: [129, 118, 38], type: "6b", chirality: "R"},
    {indices: [48, 114, 102], type: "6b", chirality: "L"},
    {indices: [43, 127, 112], type: "6b", chirality: "R"},
    {indices: [100, 108, 48], type: "6c", chirality: "L"},
    {indices: [67, 64, 38], type: "6c", chirality: "R"},
    {indices: [71, 80, 95], type: "6c", chirality: "L"},
    {indices: [43, 29, 15], type: "6c", chirality: "R"},
    {indices: [71, 99, 125, 77], type: "6d", chirality: "L"},
    {indices: [117, 64, 63, 125], type: "6d", chirality: "R"},
    {indices: [115, 145, 111, 100], type: "6d", chirality: "L"},
    {indices: [145, 37, 15, 28], type: "6d", chirality: "R"},
    {indices: [102, 114, 104, 41], type: "7a", chirality: "L"},
    {indices: [127, 50, 22, 112], type: "7a", chirality: "R"},
    {indices: [50, 146, 119, 74], type: "7a", chirality: "L"},
    {indices: [59, 118, 129, 41], type: "7a", chirality: "R"},
    {indices: [48, 49, 114], type: "7b", chirality: "L"},
    {indices: [16, 43, 112], type: "7b", chirality: "R"},
    {indices: [119, 95, 70], type: "7b", chirality: "L"},
    {indices: [118, 62, 38], type: "7b", chirality: "R"},
    {indices: [95, 80, 81], type: "7c", chirality: "L"},
    {indices: [30, 29, 43], type: "7c", chirality: "R"},
    {indices: [108, 98, 48], type: "7c", chirality: "L"},
    {indices: [38, 32, 67], type: "7c", chirality: "R"},
    {indices: [71, 58, 80], type: "7d", chirality: "L"},
    {indices: [36, 15, 29], type: "7d", chirality: "R"},
    {indices: [27, 108, 100], type: "7d", chirality: "L"},
    {indices: [67, 78, 64], type: "7d", chirality: "R"},
    {indices: [77, 66, 71], type: "7e", chirality: "L"},
    {indices: [109, 28, 15], type: "7e", chirality: "R"},
    {indices: [20, 100, 111], type: "7e", chirality: "L"},
    {indices: [64, 75, 63], type: "7e", chirality: "R"},
    {indices: [63, 56, 77, 125], type: "7f", chirality: null},
    {indices: [28, 21, 111, 145], type: "7f", chirality: null},
    {indices: [50, 0, 22], type: "8a", chirality: "L"},
    {indices: [104, 93, 41], type: "8a", chirality: "R"},
    {indices: [39, 59, 41], type: "8a", chirality: "L"},
    {indices: [47, 50, 74], type: "8a", chirality: "R"},
    {indices: [119, 70, 74], type: "8b", chirality: "L"},
    {indices: [16, 112, 22], type: "8b", chirality: "R"},
    {indices: [114, 49, 104], type: "8b", chirality: "L"},
    {indices: [59, 62, 118], type: "8b", chirality: "R"},
    {indices: [38, 62, 32], type: "8c", chirality: "L"},
    {indices: [98, 49, 48], type: "8c", chirality: "R"},
    {indices: [43, 16, 30], type: "8c", chirality: "L"},
    {indices: [95, 81, 70], type: "8c", chirality: "R"},
    {indices: [98, 108, 27, 19], type: "8d", chirality: "L"},
    {indices: [67, 32, 33, 78], type: "8d", chirality: "R"},
    {indices: [80, 58, 57, 81], type: "8d", chirality: "L"},
    {indices: [29, 30, 9, 36], type: "8d", chirality: "R"},
    {indices: [109, 15, 36], type: "8e", chirality: "L"},
    {indices: [27, 100, 20], type: "8e", chirality: "R"},
    {indices: [64, 78, 75], type: "8e", chirality: "L"},
    {indices: [71, 66, 58], type: "8e", chirality: "R"},
    {indices: [56, 66, 77], type: "8f", chirality: "L"},
    {indices: [63, 75, 56], type: "8f", chirality: "R"},
    {indices: [111, 21, 20], type: "8f", chirality: "L"},
    {indices: [21, 28, 109], type: "8f", chirality: "R"},
    {indices: [50, 47, 6, 0], type: "9a", chirality: null},
    {indices: [93, 131, 39, 41], type: "9a", chirality: null},
    {indices: [22, 0, 10], type: "9b", chirality: "L"},
    {indices: [104, 113, 93], type: "9b", chirality: "R"},
    {indices: [39, 116, 59], type: "9b", chirality: "L"},
    {indices: [74, 86, 47], type: "9b", chirality: "R"},
    {indices: [74, 70, 82], type: "9c", chirality: "L"},
    {indices: [2, 16, 22], type: "9c", chirality: "R"},
    {indices: [104, 49, 52], type: "9c", chirality: "L"},
    {indices: [59, 101, 62], type: "9c", chirality: "R"},
    {indices: [30, 16, 11, 9], type: "9d", chirality: "L"},
    {indices: [81, 57, 91, 70], type: "9d", chirality: "R"},
    {indices: [62, 133, 33, 32], type: "9d", chirality: "L"},
    {indices: [49, 98, 19, 136], type: "9d", chirality: "R"},
    {indices: [58, 83, 57], type: "9e", chirality: "L"},
    {indices: [33, 135, 78], type: "9e", chirality: "R"},
    {indices: [19, 27, 139], type: "9e", chirality: "L"},
    {indices: [36, 9, 1], type: "9e", chirality: "R"},
    {indices: [36, 144, 109], type: "9f", chirality: "L"},
    {indices: [66, 142, 58], type: "9f", chirality: "R"},
    {indices: [78, 126, 75], type: "9f", chirality: "L"},
    {indices: [27, 20, 143], type: "9f", chirality: "R"},
    {indices: [66, 56, 75, 130], type: "9g", chirality: null},
    {indices: [20, 21, 109, 103], type: "9g", chirality: null},
    {indices: [5,6,47], type: "10a", chirality: "L"},
    {indices: [18,0,6], type: "10a", chirality: "R"},
    {indices: [93, 107, 131], type: "10a", chirality: "L"},
    {indices: [65, 39, 131], type: "10a", chirality: "R"},
    {indices: [39, 44, 116], type: "10b", chirality: "L"},
    {indices: [113, 94, 93], type: "10b", chirality: "R"},
    {indices: [85, 10, 0], type: "10b", chirality: "L"},
    {indices: [47, 86, 3], type: "10b", chirality: "R"},
    {indices: [22, 10, 2], type: "10c", chirality: "L"},
    {indices: [104, 52, 113], type: "10c", chirality: "R"},
    {indices: [116, 101, 59], type: "10c", chirality: "L"},
    {indices: [74, 82, 86], type: "10c", chirality: "R"},
    {indices: [133, 62, 101], type: "10d", chirality: "L"},
    {indices: [70, 91, 82], type: "10d", chirality: "R"},
    {indices: [16, 2, 11], type: "10d", chirality: "L"},
    {indices: [52, 49, 136], type: "10d", chirality: "R"},
    {indices: [133, 79, 33], type: "10e", chirality: "L"},
    {indices: [19, 26, 136], type: "10e", chirality: "R"},
    {indices: [106, 9, 11], type: "10e", chirality: "L"},
    {indices: [57, 69, 91], type: "10e", chirality: "R"},
    {indices: [144, 36, 1, 14], type: "10f", chirality: "L"},
    {indices: [142, 89, 83, 58], type: "10f", chirality: "R"},
    {indices: [78, 135, 124, 126], type: "10f", chirality: "L"},
    {indices: [139, 27, 143, 140], type: "10f", chirality: "R"},
    {indices: [103, 109, 144, 8], type: "10g", chirality: "L"},
    {indices: [130, 87, 142, 66], type: "10g", chirality: "R"},
    {indices: [130, 75, 126, 132], type: "10g", chirality: "L"},
    {indices: [143, 20, 103, 137], type: "10g", chirality: "R"},
    {indices: [17, 18, 6, 5], type: "11a", chirality: null},
    {indices: [60, 65, 131, 107], type: "11a", chirality: null},
    {indices: [93, 94, 107], type: "11b", chirality: "L"},
    {indices: [65, 44, 39], type: "11b", chirality: "R"},
    {indices: [5, 47, 3], type: "11b", chirality: "L"},
    {indices: [85, 0, 18], type: "11b", chirality: "R"},
    {indices: [121, 101, 116, 44], type: "11c", chirality: "L"},
    {indices: [3, 86, 82, 7], type: "11c", chirality: "R"},
    {indices: [85, 40, 2, 10], type: "11c", chirality: "L"},
    {indices: [53, 94, 113, 52], type: "11c", chirality: "R"},
    {indices: [101, 72, 79, 133], type: "11d", chirality: "L"},
    {indices: [91, 69, 68, 82], type: "11d", chirality: "R"},
    {indices: [11, 2, 110, 106], type: "11d", chirality: "L"},
    {indices: [24, 52, 136, 26], type: "11d", chirality: "R"},
    {indices: [144, 14, 8], type: "11e", chirality: "L"},
    {indices: [142, 87, 89],  type: "11e", chirality: "R"},
    {indices: [126, 124, 132], type: "11e", chirality: "L"},
    {indices: [140, 143, 137], type: "11e", chirality: "R"},
    {indices: [132, 88, 87, 130], type: "11f", chirality: null},
    {indices: [8, 12, 137, 103], type: "11f", chirality: null},
    {indices: [23, 17, 5, 3], type: "12a", chirality: "L"},
    {indices: [73, 85, 18, 17], type: "12a", chirality: "R"},
    {indices: [107, 94, 61, 60], type: "12a", chirality: "L"},
    {indices: [65, 60, 105, 44], type: "12a", chirality: "R"},
    {indices: [52, 24, 25, 53], type: "12b", chirality: "L"},
    {indices: [101, 121, 76, 72], type: "12b", chirality: "R"},
    {indices: [4, 7, 82, 68], type: "12b", chirality: "L"},
    {indices: [40, 84, 110, 2], type: "12b", chirality: "R"},
    {indices: [88, 134, 87], type: "12c", chirality: "L"},
    {indices: [12, 8, 138], type: "12c", chirality: "R"},
    {indices: [137, 12, 13], type: "12c", chirality: "L"},
    {indices: [88, 132, 90], type: "12c", chirality: "R"}
];

// The stellations themselves.
export const stellations = [
    // Fully symmetric stellations
    {name: "Original rhombic triacontahedron", chiral: false, facets: ["0"]}, 
    {name: "A", chiral: false, facets: ["1a"]}, 
    {name: "B", chiral: false, facets: ["2a", "2b"]}, 
    {name: "C", chiral: false, facets: ["2a", "3a", "3c"]},
    {name: "CD", chiral: false, facets: ["3a", "3b", "3c"]}, 
    {name: "D", chiral: false, facets: ["2b", "3b"]}, 
    {name: "DE", chiral: false, facets: ["3a", "3b", "4a", "4d"]},
    {name: "DG", chiral: false, facets: ["3a", "3b", "4a", "5a"]}, 
    {name: "E", chiral: false, facets: ["2a", "3a", "4a", "4d"]}, 
    {name: "EF (compound of 5 cubes)", chiral: false, facets: ["4a", "4b", "4c", "4d"]}, 
    {name: "EI", chiral: false, facets: ["4a", "4c", "4d", "5c"]}, 
    {name: "F", chiral: false, facets: ["3c", "4b", "4c"]}, 
    {name: "FG", chiral: false, facets: ["4a", "4b", "4c", "5a"]}, 
    {name: "G (medial rhombic triacontahedron)", chiral: false, facets: ["2a", "3a", "4a", "5a"]},
    {name: "GH", chiral: false, facets: ["4b", "5a", "5b", "5d"]}, 
    {name: "GHI", chiral: false, facets: ["5a", "5b", "5c", "5d"]}, 
    {name: "GI", chiral: false, facets: ["4a", "4c", "5a", "5c"]}, 
    {name: "GK", chiral: false, facets: ["5a", "5d", "6b", "6c", "6d"]}, 
    {name: "GM", chiral: false, facets: ["5a", "5d", "6b", "6d", "7b", "7e"]}, 
    {name: "GMN", chiral: false, facets: ["5a", "5d", "6d", "7b", "7c", "7e", "7f"]}, 
    {name: "GN", chiral: false, facets: ["5a", "5d", "6c", "6d", "7c", "7f"]},
    {name: "GQ", chiral: false, facets: ["5a", "5d", "6d", "7e", "7f", "8c", "8f"]}, 
    {name: "GU", chiral: false, facets: ["5a", "5d", "6d", "7e", "7f", "8f", "9g"]}, 
    {name: "H", chiral: false, facets: ["4b", "4d", "5b", "5d"]},
    {name: "HI", chiral: false, facets: ["4d", "5b", "5c", "5d"]}, 
    {name: "I", chiral: false, facets: ["3c", "4c", "5c"]}, 
    {name: "IJ", chiral: false, facets: ["5b", "5c", "6a"]},
    {name: "J", chiral: false, facets: ["4b", "5b", "6a"]}, 
    {name: "JK", chiral: false, facets: ["6a", "6b", "6c", "6d"]}, 
    {name: "JM", chiral: false, facets: ["6a", "6b", "6d", "7b", "7e"]}, 
    {name: "JMN", chiral: false, facets: ["6a", "6d", "7b", "7c", "7e", "7f"]},
    {name: "JN", chiral: false, facets: ["6a", "6c", "6d", "7c", "7f"]}, 
    {name: "JQ", chiral: false, facets: ["6a", "6d", "7e", "7f", "8c", "8f"]},
    {name: "JU (great rhombic triacontahedron)", chiral: false, facets: ["6a", "6d", "7e", "7f", "8f", "9g"]},
    {name: "K", chiral: false, facets: ["4d", "5d", "6b", "6c", "6d"]},
    {name: "L", chiral: false, facets: ["6b", "6c", "7a", "7d"]},
    {name: "LM", chiral: false, facets: ["6b", "7a", "7b", "7d", "7e"]},
    {name: "LMN", chiral: false, facets: ["7a", "7b", "7c", "7d", "7e", "7f"]},
    {name: "LN", chiral: false, facets: ["6c", "7a", "7c", "7d", "7f"]},
    {name: "LQ", chiral: false, facets: ["7a", "7d", "7e", "7f", "8c", "8f"]},
    {name: "LU", chiral: false, facets: ["7a", "7d", "7e", "7f", "8f", "9g"]},
    {name: "M", chiral: false, facets: ["4d", "5d", "6b", "6d", "7b", "7e"]},
    {name: "MN", chiral: false, facets: ["4d", "5d", "6d", "7b", "7c", "7e", "7f"]},
    {name: "MO", chiral: false, facets: ["7b", "7c", "7d", "7e", "8a", "8d"]},
    {name: "MR", chiral: false, facets: ["7b", "7c", "7d", "7e", "8d", "9a", "9e"]},
    {name: "N", chiral: false, facets: ["4d", "5d", "6c", "6d", "7c", "7f"]},
    {name: "NP", chiral: false, facets: ["7a", "7b", "7c", "7f", "8b", "8e"]},
    {name: "NT", chiral: false, facets: ["7a", "7b", "7c", "7f", "8b", "9c"]},
    {name: "O", chiral: false, facets: ["6c", "7c", "7d", "8a", "8d"]},
    {name: "OP", chiral: false, facets: ["7b", "7c", "8a", "8b", "8d", "8e"]},
    {name: "OPQ", chiral: false, facets: ["8a", "8b", "8c", "8d", "8e", "8f"]},
    {name: "OPU", chiral: false, facets: ["8a", "8b", "8d", "8e", "8f", "9g"]},
    {name: "OQ", chiral: false, facets: ["7d", "7e", "8a", "8c", "8d", "8f"]},
    {name: "OQT", chiral: false, facets: ["8a", "8b", "8c", "8d", "8f", "9c"]},
    {name: "OT", chiral: false, facets: ["7b", "7c", "8a", "8b", "8d", "9c"]},
    {name: "OTU", chiral: false, facets: ["8a", "8b", "8d", "8f", "9c", "9g"]},
    {name: "OU", chiral: false, facets: ["7d", "7e", "8a", "8d", "8f", "9g"]},
    {name: "P", chiral: false, facets: ["6b", "7a", "7b", "8b", "8e"]},
    {name: "PQ", chiral: false, facets: ["7a", "7f", "8b", "8c", "8e", "8f"]},
    {name: "PQR", chiral: false, facets: ["8b", "8c", "8d", "8e", "8f", "9a", "9e"]},
    {name: "PR", chiral: false, facets: ["7b", "7c", "8b", "8d", "8e", "9a", "9e"]},
    {name: "PRU", chiral: false, facets: ["8b", "8d", "8e", "8f", "9a", "9e", "9g"]},
    {name: "PU", chiral: false, facets: ["7a", "7f", "8b", "8e", "8f", "9g"]},
    {name: "Q", chiral: false, facets: ["4d", "5d", "6d", "7e", "7f", "8c", "8f"]},
    {name: "QR", chiral: false, facets: ["7d", "7e", "8c", "8d", "8f", "9a", "9e"]},
    {name: "QRT", chiral: false, facets: ["8b", "8c", "8d", "8f", "9a", "9c", "9e"]},
    {name: "QT", chiral: false, facets: ["7a", "7f", "8b", "8c", "8f", "9c"]},
    {name: "R", chiral: false, facets: ["6c", "7c", "7d", "8d", "9a", "9e"]},
    {name: "RS", chiral: false, facets: ["8c", "8e", "9a", "9b", "9d", "9e", "9f"]},
    {name: "RST", chiral: false, facets: ["8c", "9a", "9b", "9c", "9d", "9e", "9f"]},
    {name: "RSTU", chiral: false, facets: ["9a", "9b", "9c", "9d", "9e", "9f", "9g"]},
    {name: "RSU", chiral: false, facets: ["8e", "9a", "9b", "9d", "9e", "9f", "9g"]},
    {name: "RT", chiral: false, facets: ["7b", "7c", "8b", "8d", "9a", "9c", "9e"]},
    {name: "RTU", chiral: false, facets: ["8b", "8d", "8f", "9a", "9c", "9e", "9g"]},
    {name: "RTW", chiral: false, facets: ["9a", "9b", "9c", "9e", "9f", "10b", "10g"]},
    {name: "RU", chiral: false, facets: ["7d", "7e", "8d", "8f", "9a", "9e", "9g"]},
    {name: "RUX", chiral: false, facets: ["9a", "9b", "9d", "9e", "9g", "10c", "10d"]},
    {name: "RW", chiral: false, facets: ["8e", "9a", "9b", "9e", "9f", "10b", "10g"]},
    {name: "RWX", chiral: false, facets: ["9a", "9b", "9e", "10b", "10c", "10d", "10g"]},
    {name: "RX", chiral: false, facets: ["8c", "9a", "9b", "9d", "9e", "10c", "10d"]},
    {name: <>R<sub>2</sub>A</>, chiral: false, facets: ["9a", "9b", "9e", "10b", "10c", "11c", "11f"]},
    {name: "S", chiral: false, facets: ["8a", "8c", "8e", "9b", "9d", "9f"]},
    {name: "ST", chiral: false, facets: ["8a", "8c", "9b", "9c", "9d", "9f"]},
    {name: "STU", chiral: false, facets: ["8a", "9b", "9c", "9d", "9f", "9g"]},
    {name: "SU", chiral: false, facets: ["8a", "8e", "9b", "9d", "9f", "9g"]},
    {name: "T", chiral: false, facets: ["6b", "7a", "7b", "8b", "9c"]},
    {name: "TU", chiral: false, facets: ["7a", "7f", "8b", "8f", "9c", "9g"]},
    {name: "TUV", chiral: false, facets: ["9a", "9c", "9d", "9f", "9g", "10a", "10e", "10f"]},
    {name: "TV", chiral: false, facets: ["8c", "9a", "9c", "9d", "9f", "10a", "10e", "10f"]},
    {name: "TVW", chiral: false, facets: ["9a", "9c", "9f", "10a", "10b", "10e", "10f", "10g"]},
    {name: "TW", chiral: false, facets: ["8a", "9b", "9c", "9f", "10b", "10g"]},
    {name: "TZ", chiral: false, facets: ["9a", "9c", "9f", "10a", "10f", "10g", "11b", "11e"]},
    {name: "U (rhombic hexecontahedron)", chiral: false, facets: ["4d", "5d", "6d", "7e", "7f", "8f", "9g"]},
    {name: "UV", chiral: false, facets: ["8e", "9a", "9d", "9f", "9g", "10a", "10e", "10f"]},
    {name: "UVX", chiral: false, facets: ["9a", "9d", "9g", "10a", "10c", "10d", "10e", "10f"]},
    {name: "UX", chiral: false, facets: ["8a", "9b", "9d", "9g", "10c", "10d"]},
    {name: "UY", chiral: false, facets: ["9a", "9d", "9g", "10a", "10d", "10e", "11a", "11d"]},
    {name: "V", chiral: false, facets: ["8c", "8e", "9a", "9d", "9f", "10a", "10e", "10f"]},
    {name: "VW", chiral: false, facets: ["8e", "9a", "9f", "10a", "10b", "10e", "10f", "10g"]},
    {name: "VWX", chiral: false, facets: ["9a", "10a", "10b", "10c", "10d", "10e", "10f", "10g"]},
    {name: "VX", chiral: false, facets: ["8c", "9a", "9d", "10a", "10c", "10d", "10e", "10f"]},
    {name: <>V<sub>2</sub>A</>, chiral: false, facets: ["9a", "10a", "10b", "10c", "10e", "10f", "11c", "11f"]},
    {name: "W", chiral: false, facets: ["8a", "8e", "9b", "9f", "10b", "10g"]},
    {name: "WX", chiral: false, facets: ["8a", "9b", "10b", "10c", "10d", "10g"]},
    {name: "WY", chiral: false, facets: ["9a", "10a", "10b", "10d", "10e", "10g", "11a", "11d"]},
    {name: "X", chiral: false, facets: ["8a", "8c", "9b", "9d", "10c", "10d"]},
    {name: "XZ", chiral: false, facets: ["9a", "10a", "10c", "10d", "10f", "10g", "11b", "11e"]},
    {name: "Y", chiral: false, facets: ["8c", "9a", "9d", "10a", "10d", "10e", "11a", "11d"]},
    {name: "YZ", chiral: false, facets: ["9a", "10a", "10d", "10g", "11a", "11b", "11d", "11e"]},
    {name: <>VZ<sub>2</sub>A</>, chiral: false, facets: ["9a", "10a", "11a", "11b", "11c", "11d", "11e", "11f"]},
    {name: <>Y<sub>2</sub>A</>, chiral: false, facets: ["9a", "10a", "10b", "10e", "11a", "11c", "11d", "11f"]},
    {name: "Z", chiral: false, facets: ["8e", "9a", "9f", "10a", "10f", "10g", "11b", "11e"]},
    {name: <>Z<sub>2</sub>A</>, chiral: false, facets: ["9a", "10a", "10c", "10f", "11b", "11c", "11e", "11f"]},
    {name: <><sub>2</sub>A</>, chiral: false, facets: ["8a", "9b", "10b", "10c", "11c", "11f"]},
    {name: <><sub>2</sub>B (final stellation)</>, chiral: false, facets: ["9a", "10a", "11a", "11b", "11f", "12a", "12b", "12c"]},
    // Chiral stellations (one enantiomorph each)
    {name: <>EF<sup>R</sup></>, chiral: true, facets: ["R4b", "R4c", "L3a", "L3b", "4a", "4d"]},
    {name: <>F<sup>R</sup></>, chiral: true, facets: ["R4b", "R4c", "L3a", "L3b", "3c"]},
    {name: <>F<sup>R</sup>G</>, chiral: true, facets: ["R4b", "R4c", "L3a", "L3b", "4a", "5a"]},
    {name: <>GK<sup>R</sup></>, chiral: true, facets: ["R6b", "R6c", "R6d", "L5b", "L5c", "5a", "5d"]},
    {name: <>GQ<sup>R</sup></>, chiral: true, facets: ["R8c", "R8f", "L7b", "L7c", "5a", "5d", "6d", "7e", "7f"]},
    {name: <>JK<sup>R</sup></>, chiral: true, facets: ["R6b", "R6c", "R6d", "L5b", "L5c", "6a"]},
    {name: <>JQ<sup>R</sup></>, chiral: true, facets: ["R8c", "R8f", "L7b", "L7c", "6a", "6d", "7e", "7f"]},
    {name: <>K<sup>R</sup></>, chiral: true, facets: ["R6b", "R6c", "R6d", "L5b", "L5c", "4d", "5d"]},
    {name: <>LQ<sup>R</sup></>, chiral: true, facets: ["R8c", "R8f", "L7b", "L7c", "7a", "7d", "7e", "7f"]},
    {name: <>NP<sup>R</sup></>, chiral: true, facets: ["R8b", "R8e", "L7d", "L7e", "7a", "7b", "7c", "7f"]},
    {name: <>OP<sup>R</sup></>, chiral: true, facets: ["R8b", "R8e", "L7d", "L7e", "7b", "7c", "8a", "8d"]},
    {name: <>OP<sup>R</sup>Q<sup>R</sup></>, chiral: true, facets: ["R8b", "R8c", "R8e", "R8f", "L7b", "L7c", "L7d", "L7e", "8a", "8d"]},
    {name: <>OP<sup>R</sup>Q<sup>L</sup></>, chiral: true, facets: ["R7b", "R7c", "R8b", "R8e", "L7d", "L7e", "L8c", "L8f", "8a", "8d"]},
    {name: <>OP<sup>R</sup>Q</>, chiral: true, facets: ["R8b", "R8e", "L7d", "L7e", "8a", "8c", "8d", "8f"]},
    {name: <>OPQ<sup>R</sup></>, chiral: true, facets: ["R8c", "R8f", "L7b", "L7c", "8a", "8b", "8d", "8e"]},
    {name: <>OP<sup>R</sup>U</>, chiral: true, facets: ["R8b", "R8e", "L7d", "L7e", "8a", "8d", "8f", "9g"]},
    {name: <>OQ<sup>R</sup></>, chiral: true, facets: ["R8c", "R8f", "L7b", "L7c", "7d", "7e", "8a", "8d"]},
    {name: <>OQ<sup>R</sup>T</>, chiral: true, facets: ["R8c", "R8f", "L7b", "L7c", "8a", "8b", "8d", "9c"]}, 
    {name: <>P<sup>R</sup></>, chiral: true, facets: ["R8b", "R8e", "L7d", "L7e", "6b", "7a", "7b"]},
    {name: <>P<sup>R</sup>Q<sup>R</sup></>, chiral: true, facets: ["R8b", "R8c", "R8e", "R8f", "L7b", "L7c", "L7d", "L7e", "7a", "7f"]},
    {name: <>P<sup>R</sup>Q<sup>L</sup></>, chiral: true, facets: ["R7b", "R7c", "R8b", "R8e", "L7d", "L7e", "L8c", "L8f", "7a", "7f"]},
    {name: <>P<sup>R</sup>Q</>, chiral: true, facets: ["R8b", "R8e", "L7d", "L7e", "7a", "7f", "8c", "8f"]},
    {name: <>PQ<sup>R</sup></>, chiral: true, facets: ["R8c", "R8f", "L7b", "L7c", "7a", "7f", "8b", "8e"]},
    {name: <>P<sup>R</sup>Q<sup>R</sup>R</>, chiral: true, facets: ["R8b", "R8c", "R8e", "R8f", "L7b", "L7c", "L7d", "L7e", "8d", "9a", "9e"]},
    {name: <>P<sup>R</sup>Q<sup>L</sup>R</>, chiral: true, facets: ["R7b", "R7c", "R8b", "R8e", "L7d", "L7e", "L8c", "L8f", "8d", "9a", "9e"]}, 
    {name: <>P<sup>R</sup>QR</>, chiral: true, facets: ["R8b", "R8e", "L7d", "L7e", "8c", "8d", "8f", "9a", "9e"]},
    {name: <>PQ<sup>R</sup>R</>, chiral: true, facets: ["R8c", "R8f", "L7b", "L7c", "8b", "8d", "8e", "9a", "9e"]},
    {name: <>P<sup>R</sup>R</>, chiral: true, facets: ["R8b", "R8e", "L7d", "L7e", "7b", "7c", "8d", "9a", "9e"]},
    {name: <>P<sup>R</sup>RU</>, chiral: true, facets: ["R8b", "R8e", "L7d", "L7e", "8d", "8f", "9a", "9e", "9g"]},
    {name: <>P<sup>R</sup>U</>, chiral: true, facets: ["R8b", "R8e", "L7d", "L7e", "7a", "7f", "8f", "9g"]},
    {name: <>Q<sup>R</sup></>, chiral: true, facets: ["R8c", "R8f", "L7b", "L7c", "4d", "5d", "6d", "7e", "7f"]},
    {name: <>Q<sup>R</sup>R</>, chiral: true, facets: ["R8c", "R8f", "L7b", "L7c", "7d", "7e", "8d", "9a", "9e"]},
    {name: <>Q<sup>R</sup>RT</>, chiral: true, facets: ["R8c", "R8f", "L7b", "L7c", "8b", "8d", "9a", "9c", "9e"]},
    {name: <>Q<sup>R</sup>T</>, chiral: true, facets: ["R8c", "R8f", "L7b", "L7c", "7a", "7f", "8b", "9c"]},
    {name: <>P<sup>R</sup>Q<sup>R</sup>RS<sup>R</sup></>, chiral: true, facets: ["R8c", "R8e", "R9b", "R9d", "R9f", "L7b", "L7c", "L7d", "L7e", "L8d", "9a", "9e"]},
    {name: <>P<sup>R</sup>RS<sup>R</sup></>, chiral: true, facets: ["R8e", "R9d", "R9b", "R9f", "L7d", "L7e", "L8d", "L8f", "8c", "9a", "9e"]},
    {name: <>Q<sup>R</sup>RS<sup>R</sup></>, chiral: true, facets: ["R8c", "R9d", "R9b", "R9f", "L7b", "L7c", "L8b", "L8d", "8e", "9a", "9e"]},
    {name: <>RS<sup>R</sup></>, chiral: true, facets: ["R9b", "R9d", "R9f", "L8b", "L8d", "L8f", "8c", "8e", "9a", "9e"]},
    {name: <>Q<sup>R</sup>RS<sup>R</sup>T</>, chiral: true, facets: ["R8c", "R9b", "R9d", "R9f", "L7b", "L7c", "L8b", "L8d", "9a", "9c", "9e"]},
    {name: <>RS<sup>R</sup>T</>, chiral: true, facets: ["R9b", "R9d", "R9f", "L8b", "L8d", "L8f", "8c", "9a", "9c", "9e"]},
    {name: <>RS<sup>R</sup>TU</>, chiral: true, facets: ["R9b", "R9d", "R9f", "L8b", "L8d", "L8f", "9a", "9c", "9e", "9g"]},
    {name: <>P<sup>R</sup>RS<sup>R</sup>U</>, chiral: true, facets: ["R8e", "R9b", "R9d", "R9f", "L7d", "L7e", "L8d", "L8f", "9a", "9e", "9g"]},
    {name: <>RS<sup>R</sup>U</>, chiral: true, facets: ["R9b", "R9d", "R9f", "L8b", "L8d", "L8f", "8e", "9a", "9e", "9g"]},
    {name: <>RS<sup>R</sup>UX<sup>R</sup></>, chiral: true, facets: ["R9b", "R9d", "R10c", "R10d", "L8b", "L8d", "L8f", "L9c", "9a", "9e", "9g"]},
    {name: <>RUX<sup>R</sup></>, chiral: true, facets: ["R10c", "R10d", "L9c", "L9f", "9a", "9b", "9d", "9e", "9g"]},
    {name: <>RWX<sup>R</sup></>, chiral: true, facets: ["R10c", "R10d", "L9c", "L9f", "9a", "9b", "9e", "10b", "10g"]},
    {name: <>Q<sup>R</sup>RS<sup>R</sup>X<sup>R</sup></>, chiral: true, facets: ["R8c", "R9b", "R9d", "R10c", "R10d", "L7b", "L7c", "L8b", "L8d", "L9c", "9a", "9e"]},
    {name: <>RS<sup>R</sup>X<sup>R</sup></>, chiral: true, facets: ["R9b", "R9d", "R10c", "R10d", "L8b", "L8d", "L8f", "L9c", "8c", "9a", "9e"]},
    {name: <>RX<sup>R</sup></>, chiral: true, facets: ["R10c", "R10d", "L9c", "L9f", "8c", "9a", "9b", "9d", "9e"]},
    {name: <>P<sup>R</sup>Q<sup>R</sup>S<sup>R</sup></>, chiral: true, facets: ["R8c", "R8e", "R9b", "R9d", "R9f", "L7b", "L7c", "L7d", "L7e", "L8d", "8a"]},
    {name: <>P<sup>R</sup>S<sup>R</sup></>, chiral: true, facets: ["R8e", "R9b", "R9d", "R9f", "L7d", "L7e", "L8d", "L8f", "8a", "8c"]},
    {name: <>Q<sup>R</sup>S<sup>R</sup></>, chiral: true, facets: ["R8c", "R9b", "R9d", "R9f", "L7b", "L7c", "L8b", "L8d", "8a", "8e"]},
    {name: <>S<sup>R</sup></>, chiral: true, facets: ["R9b", "R9d", "R9f", "L8b", "L8d", "L8f", "8a", "8c", "8e"]},
    {name: <>Q<sup>R</sup>S<sup>R</sup>T</>, chiral: true, facets: ["R8c", "R9b", "R9d", "R9f", "L7b", "L7c", "L8b", "L8d", "8a", "9c"]},
    {name: <>S<sup>R</sup>T</>, chiral: true, facets: ["R9b", "R9d", "R9f", "L8b", "L8d", "L8f", "8a", "8c", "9c"]},
    {name: <>S<sup>R</sup>TU</>, chiral: true, facets: ["R9b", "R9d", "R9f", "L8b", "L8d", "L8f", "8a", "9c", "9g"]},
    {name: <>P<sup>R</sup>S<sup>R</sup>U</>, chiral: true, facets: ["R8e", "R9b", "R9d", "R9f", "L7d", "L7e", "L8d", "L8f", "8a", "9g"]},
    {name: <>S<sup>R</sup>U</>, chiral: true, facets: ["R9b", "R9d", "R9f", "L8b", "L8d", "L8f", "8a", "8e", "9g"]},
    {name: <>S<sup>R</sup>TUV<sup>R</sup></>, chiral: true, facets: ["R9d", "R9f", "R10a", "R10e", "R10f", "L8b", "L8d", "L8f", "L9e", "9a", "9c", "9g"]},
    {name: <>TUV<sup>R</sup></>, chiral: true, facets: ["R10a", "R10e", "R10f", "L9b", "L9e", "9a", "9c", "9d", "9f", "9g"]},
    {name: <>Q<sup>R</sup>S<sup>R</sup>TV<sup>R</sup></>, chiral: true, facets: ["R8c", "R9d", "R9f", "R10a", "R10e", "R10f", "L7b", "L7c", "L8b", "L8d", "L9e", "9a", "9c"]},
    {name: <>S<sup>R</sup>TV<sup>R</sup></>, chiral: true, facets: ["R9d", "R9f", "R10a", "R10e", "R10f", "L8b", "L8d", "L8f", "L9e", "8c", "9a", "9c"]},
    {name: <>TV<sup>R</sup></>, chiral: true, facets: ["R10a", "R10e", "R10f", "L9b", "L9e", "L9t", "8c", "9a", "9c", "9d", "9f"]},
    {name: <>TV<sup>R</sup>W</>, chiral: true, facets: ["R10a", "R10e", "R10f", "L9b", "L9e", "9a", "9c", "9f", "10b", "10g"]},
    {name: <>TV<sup>R</sup>Z<sup>R</sup></>, chiral: true, facets: ["R10a", "R10f", "R11b", "R11e", "L9b", "L9e", "L10b", "9a", "9c", "9f", "10g"]},
    {name: <>TZ<sup>R</sup></>, chiral: true, facets: ["R11b", "R11e", "L10b", "L10e", "9a", "9c", "9f", "10a", "10f", "10g"]},
    {name: <>P<sup>R</sup>S<sup>R</sup>UV<sup>R</sup></>, chiral: true, facets: ["R8e", "R9d", "R9f", "R10a", "R10e", "R10f", "L7d", "L7e", "L8d", "L8f", "L9e", "9a", "9g"]},
    {name: <>S<sup>R</sup>UV<sup>R</sup></>, chiral: true, facets: ["R9d", "R9f", "R10a", "R10e", "R10f", "L8b", "L8d", "L8f", "L9e", "8e", "9a", "9g"]},
    {name: <>UV<sup>R</sup></>, chiral: true, facets: ["R10a", "R10e", "R10f", "L9b", "L9e", "8e", "9a", "9d", "9f", "9g"]},
    {name: <>S<sup>R</sup>UV<sup>R</sup>X<sup>R</sup></>, chiral: true, facets: ["R9d", "R10a", "R10c", "R10d", "R10e", "R10f", "L8b", "L8d", "L8f", "L9c", "L9e", "9a", "9g"]},
    {name: <>UV<sup>R</sup>X<sup>R</sup></>, chiral: true, facets: ["R10a", "R10c", "R10d", "R10e", "R10f", "L9b", "L9c", "L9e", "L9f", "9a", "9d", "9g"]}, // This one had an error in the original paper
    {name: <>UV<sup>L</sup>X<sup>R</sup></>, chiral: true, facets: ["R9b", "R9e", "R10c", "R10d", "L9c", "L9f", "L10a", "L10e", "L10f", "9a", "9d", "9g"]},
    {name: <>UVX<sup>R</sup></>, chiral: true, facets: ["R10c", "R10d", "L9c", "L9f", "9a", "9d", "9g", "10a", "10e", "10f"]}, 
    {name: <>UV<sup>R</sup>X</>, chiral: true, facets: ["R10a", "R10e", "R10f","L9b", "L9e", "9a", "9d", "9g", "10c", "10d"]}, 
    {name: <>S<sup>R</sup>UX<sup>R</sup></>, chiral: true, facets: ["R9b", "R9d", "R10c", "R10d", "L8b", "L8d", "L8f", "L9c", "8a", "9g"]},
    {name: <>UX<sup>R</sup></>, chiral: true, facets: ["R10c", "R10d", "L9c", "L9f", "8a", "9b", "9d", "9g"]},
    {name: <>P<sup>R</sup>Q<sup>R</sup>S<sup>R</sup>V<sup>R</sup></>, chiral: true, facets: ["R8c", "R8e", "R9d", "R9f", "R10a", "R10e", "R10f", "L7b", "L7c", "L7d", "L7e", "L8d", "L9e", "9a"]},
    {name: <>P<sup>R</sup>S<sup>R</sup>V<sup>R</sup></>, chiral: true, facets: ["R8e", "R9d", "R9f", "R10a", "R10e", "R10f", "L7d", "L7e", "L8d", "L8f", "L9e", "8c", "9a"]},
    {name: <>Q<sup>R</sup>S<sup>R</sup>V<sup>R</sup></>, chiral: true, facets: ["R8c", "R9d", "R9f", "R10a", "R10e", "R10f", "L7b", "L7c", "L8b", "L8d", "L9e", "8e", "9a"]},
    {name: <>S<sup>R</sup>V<sup>R</sup></>, chiral: true, facets: ["R9d", "R9f", "R10a", "R10e", "R10f", "L8b", "L8d", "L8f", "L9e", "8c", "8e", "9a"]},
    {name: <>V<sup>R</sup></>, chiral: true, facets: ["R10a", "R10e", "R10f", "L9b", "L9e", "8c", "8e", "9a", "9d", "9f"]},
    {name: <>V<sup>R</sup>W</>, chiral: true, facets: ["R10a", "R10e", "R10f", "L9b", "L9e", "8e", "9a", "9f", "10b", "10g"]},
    {name: <>V<sup>R</sup>WX<sup>R</sup></>, chiral: true, facets: ["R10a", "R10c", "R10d", "R10e", "R10f", "L9b", "L9c", "L9e", "L9f", "9a", "10b", "10g"]},
    {name: <>V<sup>L</sup>WX<sup>R</sup></>, chiral: true, facets: ["R9b", "R9e", "R10c", "R10d", "L9c", "L9f", "L10a", "L10e", "L10f", "9a", "10b", "10g"]},
    {name: <>VWX<sup>R</sup></>, chiral: true, facets: ["R10c", "R10d", "L9c", "L9f", "9a", "10a", "10b", "10e", "10f", "10g"]},
    {name: <>V<sup>R</sup>WX</>, chiral: true, facets: ["R10a", "R10e", "R10f", "L9b", "L9e", "9a", "10b", "10c", "10d", "10g"]},
    {name: <>Q<sup>R</sup>S<sup>R</sup>V<sup>R</sup>X<sup>R</sup></>, chiral: true, facets: ["R8c", "R9d", "R10a", "R10c", "R10d", "R10e", "R10f", "L7b", "L7c", "L8b", "L8d", "L9c", "L9e", "9a"]},
    {name: <>S<sup>R</sup>V<sup>R</sup>X<sup>R</sup></>, chiral: true, facets: ["R9d", "R10a", "R10c", "R10d", "R10e", "R10f", "L8b", "L8d", "L8f", "L9c", "L9e", "8c", "9a"]},
    {name: <>V<sup>R</sup>X<sup>R</sup></>, chiral: true, facets: ["R10a", "R10c", "R10d", "R10e", "R10f", "L9b", "L9c", "L9e", "L9f", "8c", "9a", "9d"]}, 
    {name: <>V<sup>L</sup>X<sup>R</sup></>, chiral: true, facets: ["R9b", "R9e", "R10c", "R10d", "L9c", "L9f", "L10a", "L10e", "L10f", "8c", "9a", "9d"]},
    {name: <>VX<sup>R</sup></>, chiral: true, facets: ["R10c", "R10d", "L9c", "L9f", "8c", "9a", "9d", "10a", "10e", "10f"]},
    {name: <>V<sup>R</sup>X</>, chiral: true, facets: ["R10a", "R10e", "R10f", "L9b", "L9e", "8c", "9a", "9d", "10c", "10d"]},
    {name: <>V<sup>R</sup><sub>2</sub>A</>, chiral: true, facets: ["R10a", "R10e", "R10f", "L9b", "L9e", "9a", "10b", "10c", "11c", "11f"]}, 
    {name: <>WX<sup>R</sup></>, chiral: true, facets: ["R10c", "R10d", "L9c", "L9f", "8a", "9b", "10b", "10g"]}, 
    {name: <>Q<sup>R</sup>S<sup>R</sup>X<sup>R</sup></>, chiral: true, facets: ["R8c", "R9b", "R9d", "R10c", "R10d", "L7b", "L7c", "L8b", "L8d", "L9c", "8a"]},
    {name: <>S<sup>R</sup>X<sup>R</sup></>, chiral: true, facets: ["R9b", "R9d", "R10c", "R10d", "L8b", "L8d", "L8f", "L9c", "8a", "8c"]},
    {name: <>X<sup>R</sup></>, chiral: true, facets: ["R10c", "R10d", "L9c", "L9f", "8a", "8c", "9b", "9d"]},
    {name: <>V<sup>R</sup>X<sup>R</sup>Z<sup>R</sup></>, chiral: true, facets: ["R10a", "R10c", "R10d", "R10f", "R11b", "R11e", "L9b", "L9c", "L9e", "L9f", "L10b", "9a", "10g"]},
    {name: <>V<sup>R</sup>X<sup>L</sup>Z<sup>R</sup></>, chiral: true, facets: ["R9c", "R9f", "R10a", "R10f", "R11b", "R11e", "L9b", "L9e", "L10b", "L10c", "L10d", "9a", "10g"]}, 
    {name: <>V<sup>R</sup>XZ<sup>R</sup></>, chiral: true, facets: ["R10a", "R10f", "R11b", "R11e", "L9b", "L9e", "L10b", "9a", "10c", "10d", "10g"]},
    {name: <>X<sup>R</sup>Z<sup>R</sup></>, chiral: true, facets: ["R10c", "R10d", "R11b", "R11e", "L9c", "L9f", "L10b", "L10e", "9a", "10a", "10f", "10g"]},
    {name: <>X<sup>L</sup>Z<sup>R</sup></>, chiral: true, facets: ["R9c", "R9f", "R11b", "R11e", "L10b", "L10c", "L10d", "L10e", "9a", "10a", "10f", "10g"]},
    {name: <>XZ<sup>R</sup></>, chiral: true, facets: ["R11b", "R11e", "L10b", "L10e", "9a", "10a", "10c", "10d", "10f", "10g"]},
    {name: <>X<sup>R</sup>Z</>, chiral: true, facets: ["R10c", "R10d", "L9c", "L9f", "9a", "10a", "10f", "10g", "11b", "11e"]},
    {name: <>YZ<sup>R</sup></>, chiral: true, facets: ["R11b", "R11e", "L10b", "L10e", "9a", "10a", "10d", "10g", "11a", "11d"]},
    {name: <>YZ<sup>R</sup><sub>2</sub>A</>, chiral: true, facets: ["R11b", "R11e", "L10b", "L10e", "9a", "10a", "11a", "11c", "11d", "11f"]},
    {name: <>V<sup>R</sup>Z<sup>R</sup></>, chiral: true, facets: ["R10a", "R10f", "R11b", "R11e", "L9b", "L9e", "L10b", "8e", "9a", "9f", "10g"]},
    {name: <>Z<sup>R</sup></>, chiral: true, facets: ["R11b", "R11e", "L10b", "L10e", "8e", "9a", "9f", "10a", "10f", "10g"]},
    {name: <>V<sup>R</sup>Z<sup>R</sup><sub>2</sub>A</>, chiral: true, facets: ["R10a", "R10f", "R11b", "R11e", "L9b", "L9e", "L10b", "9a", "10c", "11c", "11f"]},
    {name: <>Z<sup>R</sup><sub>2</sub>A</>, chiral: true, facets: ["R11b", "R11e", "L10b", "L10e", "9a", "10a", "10c", "10f", "11c", "11f"]},
    {name: <>Z<sup>R</sup><sub>2</sub>B<sup>R</sup></>, chiral: true, facets: ["R11b", "R12a", "R12b", "R12c", "L10b", "L10e", "L11c", "L11d", "9a", "10a", "11a", "11f"]},
    {name: <><sub>2</sub>B<sup>R</sup></>, chiral: true, facets: ["R12a", "R12b", "R12c", "L11c", "L11d", "L11e", "9a", "10a", "11a", "11b", "11f"]}
];